import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";
import type { CreateFormFields } from "../../forms/rotations/createRotationForm";
import { withLocalTime } from "@/utils/rotations/window";
import type { EditFormFields } from "../../forms/rotations/editRotationForm";

export const useRotations = ({
  archived = false,
}: { archived?: boolean } = {}) => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    rotations: boolean;
    createRotation: boolean;
    updateRotation: boolean;
    deleteRotation: boolean;
    assignPreferences: boolean;
    archiveRotation: boolean;
    unarchiveRotation: boolean;
  }>({
    rotations: false,
    createRotation: false,
    updateRotation: false,
    deleteRotation: false,
    assignPreferences: false,
    archiveRotation: false,
    unarchiveRotation: false,
  });

  const loadRotations = async (
    setRotations: (data: { rotations: any[]; sessions: any[] }) => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, rotations: true }));
      const response = await api(
        `/rotations?shape=all${archived ? "&archived=true" : ""}`,
        { method: "GET" },
        dispatch,
      );
      setRotations({
        rotations: response.rotations,
        sessions: response.sessions,
      });
    } catch (error: any) {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, rotations: false }));
    }
  };

  const createRotation = async (
    data: CreateFormFields,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, createRotation: true }));
      const payload = {
        ...data,
        studentsCanView: {
          from: withLocalTime(
            data.studentsCanView?.from!,
            data.studentsCanView?.fromTime!,
          ),
          to: withLocalTime(
            data.studentsCanView?.to!,
            data.studentsCanView?.toTime!,
          ),
        },
        studentsCanEdit: {
          from: withLocalTime(
            data.studentsCanEdit?.from!,
            data.studentsCanEdit?.fromTime!,
          ),
          to: withLocalTime(
            data.studentsCanEdit?.to!,
            data.studentsCanEdit?.toTime!,
          ),
        },
      };

      delete (payload.studentsCanView as any)?.fromTime;
      delete (payload.studentsCanView as any)?.toTime;
      delete (payload.studentsCanEdit as any)?.fromTime;
      delete (payload.studentsCanEdit as any)?.toTime;

      const response = await api(
        "/rotations",
        { method: "POST", body: payload },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return { rotation: response.rotation, sessions: response.sessions };
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, createRotation: false }));
    }
  };

  const updateRotation = async (
    data: EditFormFields,
    rotationId: string,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, updateRotation: true }));
      const payload = {
        ...data,
        studentsCanView: {
          from: withLocalTime(
            data.studentsCanView?.from!,
            data.studentsCanView?.fromTime!,
          ),
          to: withLocalTime(
            data.studentsCanView?.to!,
            data.studentsCanView?.toTime!,
          ),
        },
        studentsCanEdit: {
          from: withLocalTime(
            data.studentsCanEdit?.from!,
            data.studentsCanEdit?.fromTime!,
          ),
          to: withLocalTime(
            data.studentsCanEdit?.to!,
            data.studentsCanEdit?.toTime!,
          ),
        },
      };

      delete (payload.studentsCanView as any)?.fromTime;
      delete (payload.studentsCanView as any)?.toTime;
      delete (payload.studentsCanEdit as any)?.fromTime;
      delete (payload.studentsCanEdit as any)?.toTime;

      const response = await api(
        `/rotations/${rotationId}`,
        { method: "PUT", body: payload },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return { rotation: response.rotation, sessions: response.sessions };
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, updateRotation: false }));
    }
  };

  const deleteRotation = async (rotationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, deleteRotation: true }));
      const response = await api(
        `/rotations/${rotationId}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success(response.message);
      return { rotationId: response.rotationId, sessionIds: response.sessions };
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, deleteRotation: false }));
    }
  };

  const assignPreferences = async (
    sessions: string[],
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, assignPreferences: true }));
      const response = await api(
        `/enrollments/assign/random`,
        { method: "POST", body: { sessions } },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, assignPreferences: false }));
    }
  };

  const archiveRotation = async (rotationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, archiveRotation: true }));
      const response = await api(
        `/rotations/${rotationId}/archive`,
        { method: "POST" },
        dispatch,
      );
      toast.success(response.message || "Rotation archived");
      return { rotationId };
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, archiveRotation: false }));
    }
  };

  const unarchiveRotation = async (rotationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, unarchiveRotation: true }));
      const response = await api(
        `/rotations/${rotationId}/unarchive`,
        { method: "POST" },
        dispatch,
      );
      toast.success(response.message || "Rotation restored");
      return { rotationId };
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, unarchiveRotation: false }));
    }
  };

  return {
    loadRotations,
    createRotation,
    updateRotation,
    deleteRotation,
    assignPreferences,
    archiveRotation,
    unarchiveRotation,
    loading,
  };
};
